// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Card, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
// Components, Layouts, Pages
import { BaseLabel, UnderDevelopment } from '~/components/common';
// Others
import { IReportItemDashboard } from '~/utils/interface/reports';
import { ReportItemEnum, RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { StorageEnum } from '~/utils/enum/base';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './ItemReport.module.scss';

type Props = {
  item: IReportItemDashboard;
};

const cx = classNames.bind(styles);

const ItemReport = (props: Props) => {
  //#region Destructuring Props
  const { item } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('common/base');
  const navigate = useNavigate();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleNavigateReport = () => {
    switch (item.type) {
      case ReportItemEnum.OVERBOOKING_ITEMS:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.overbookingItem);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.overbookingItem);
            break;
        }
        break;
      case ReportItemEnum.SUBRENTALS:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.subrentalsReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.subrentalsReport);
            break;
        }
        break;
      case ReportItemEnum.CLIENT_INCOME:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.clientIncomeReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.clientIncomeReport);
            break;
        }
        break;
      case ReportItemEnum.REVENUE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.revenueReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.revenueReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.revenueReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.revenueReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.revenueReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.revenueReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.revenueReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.revenueReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.revenueReport);
            break;
        }
        break;
      case ReportItemEnum.QUARANTINE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.quarantineReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.quarantineReport);
            break;
        }
        break;
      case ReportItemEnum.GEOGRAPHIC_REVENUE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.geographicRevenueReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.geographicRevenueReport);
            break;
        }
        break;
      case ReportItemEnum.QUOTE_REVENUE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.quoteRevenueReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.quoteRevenueReport);
            break;
        }
        break;
      case ReportItemEnum.ORDER_REVENUE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.orderRevenueReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.orderRevenueReport);
            break;
        }
        break;
      case ReportItemEnum.CANCELLATIONS:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.cancellationReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.cancellationReport);
            break;
        }
        break;
      case ReportItemEnum.DELIVERY_PICKUP:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.deliveryReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.deliveryReport);
            break;
        }
        break;
      case ReportItemEnum.USER_TASKS:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.taskCompletionReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.taskCompletionReport);
            break;
        }
        break;
      case ReportItemEnum.DISCOUNT_USAGE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.discountUsageReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.discountUsageReport);
            break;
        }
        break;
      case ReportItemEnum.FUTURE_UTILIZATION:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.featureUtilizationReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.featureUtilizationReport);
            break;
        }
        break;
      case ReportItemEnum.JOB_COSTING:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.jobCostingReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.jobCostingReport);
            break;
        }
        break;
      case ReportItemEnum.INVENTORY_USAGE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.inventoryUsageReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.inventoryUsageReport);
            break;
        }
        break;
      case ReportItemEnum.FOREMAN_TASK_COMPLETION:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.foremanTaskReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.foremanTaskReport);
            break;
        }
        break;
      case ReportItemEnum.MARKETING_REVENUE:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.marketingRevenueReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.marketingRevenueReport);
            break;
        }
        break;
      case ReportItemEnum.FINANCIAL_ORDER:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.financialOrderReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.financialOrderReport);
            break;
        }
        break;
      case ReportItemEnum.TRANSACTION_SUMMARY:
        switch (currentRole) {
          case RoleEnum.SUPER_ADMIN:
            navigate(adminRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.SALES_ADMIN:
            navigate(salesAdminRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.SALES_PERSON:
            navigate(salesPersonRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.OPERATIONS_MANAGER:
            navigate(operationsManagerRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.WAREHOUSE_LEAD:
            navigate(warehouseLeadRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.WAREHOUSE_STAFF:
            navigate(warehouseStaffRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.FOREMAN:
            navigate(foremanRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.CREW:
            navigate(crewRouteAbsolute.transactionSummaryReport);
            break;
          case RoleEnum.LOADER:
            navigate(loaderRouteAbsolute.transactionSummaryReport);
            break;
        }
        break;
      default:
        handleShowUnderDevelopment();
        break;
    }
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Card className={cx('container', item.type)} onClick={handleNavigateReport}>
      <Stack direction={'column'} spacing={4}>
        <Stack
          className={cx('icon', item.type)}
          direction={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          width={48}
          height={48}
        >
          <img src={item.icon} alt={t('common_alt_img')} className={cx('icon')} />
        </Stack>

        <Stack direction={'column'} spacing={'10px'}>
          <BaseLabel label={item.title} styleType='title' />

          <Typography className={cx('description')}>{item.description}</Typography>
        </Stack>
      </Stack>
      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Card>
  );
};

export default ItemReport;
